/**
 * TeamZoneSummaryStrip (EPIC-110 D6): "{TEAM} | {N}% Ataque Derecho" title plus
 * three ring gauges (safe / middle / dangerous zone) on the team's kit-primary
 * background. Text is white or slate-900, whichever reaches >= 4.5:1; the ring
 * arc uses the same colour (>= 3:1 against the card).
 */
import { useMemo, type FC } from "react";
import TeamLogo from "../../../../components/TeamLogo";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Team } from "../../types";
import type { TeamSide } from "../../utils/teamControlMap";
import {
  computeTeamZoneSummary,
  readableTextColor,
  stripBackground,
} from "../../utils/teamZoneSummary";

interface TeamZoneSummaryStripProps {
  team: Team;
  side: TeamSide;
  events: MatchEvent[];
  /** Kit-primary colour (hex). */
  color: string;
  t: (key: string, fallback?: string) => string;
  "data-testid"?: string;
}

const RADIUS = 15;
const CIRC = 2 * Math.PI * RADIUS;

const Gauge: FC<{
  testId?: string;
  label: string;
  value: number;
  fg: string;
}> = ({ testId, label, value, fg }) => (
  <div
    className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center sm:flex-row sm:gap-2 sm:text-left"
    data-testid={testId}
    data-value={value}
  >
    <svg viewBox="0 0 40 40" className="h-10 w-10 shrink-0" aria-hidden="true">
      <circle
        cx={20}
        cy={20}
        r={RADIUS}
        fill="none"
        stroke={fg}
        strokeOpacity={0.3}
        strokeWidth={4}
      />
      <circle
        cx={20}
        cy={20}
        r={RADIUS}
        fill="none"
        stroke={fg}
        strokeWidth={4}
        strokeLinecap="round"
        strokeDasharray={`${(value / 100) * CIRC} ${CIRC}`}
        transform="rotate(-90 20 20)"
        data-arc-color={fg}
      />
    </svg>
    <span className="text-xs font-semibold leading-tight">{`${label}: ${value}%`}</span>
  </div>
);

const TeamZoneSummaryStrip: FC<TeamZoneSummaryStripProps> = ({
  team,
  side,
  events,
  color,
  t,
  "data-testid": testId,
}) => {
  const summary = useMemo(
    () => computeTeamZoneSummary(events, team.id, side),
    [events, team.id, side],
  );
  const bg = stripBackground(color);
  const fg = readableTextColor(bg);
  const teamLabel = team.short_name || team.name;
  const sub = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);

  const laneLabel = {
    left: t("analytics.zoneStripLaneLeft", "Left attack"),
    center: t("analytics.zoneStripLaneCenter", "Central attack"),
    right: t("analytics.zoneStripLaneRight", "Right attack"),
  }[summary.lane];

  return (
    <div
      className="rounded-lg px-3 py-2"
      style={{ backgroundColor: bg, color: fg }}
      data-testid={testId}
      data-text-color={fg}
      data-bg-color={bg}
    >
      {summary.total === 0 ? (
        <p className="text-sm font-semibold" data-testid={sub("empty")}>
          {t("analytics.zoneStripEmpty", "No location data")}
        </p>
      ) : (
        <>
          <div className="mb-1 flex items-center gap-2">
            <span className="inline-flex shrink-0" data-testid={sub("crest")}>
              <TeamLogo team={team} size="xs" bare />
            </span>
            <p
              className="text-sm font-bold"
              data-testid={sub("title")}
            >{`${teamLabel} | ${summary.lanePct}% ${laneLabel}`}</p>
          </div>
          <div className="flex gap-x-2 gap-y-1 sm:flex-wrap sm:gap-x-3">
            <Gauge
              testId={sub("safe")}
              label={t("analytics.controlMapSafeZone", "Safe zone")}
              value={summary.thirds.safe}
              fg={fg}
            />
            <Gauge
              testId={sub("middle")}
              label={t("analytics.controlMapMiddleZone", "Middle zone")}
              value={summary.thirds.middle}
              fg={fg}
            />
            <Gauge
              testId={sub("danger")}
              label={t("analytics.controlMapDangerZone", "Dangerous attack")}
              value={summary.thirds.danger}
              fg={fg}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TeamZoneSummaryStrip;
